import { useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import {
  DrawerContent,
  DrawerFooter,
  DrawerTopBar,
  DrawerTopBarActionButton,
  SwipeableDrawer,
} from "@/components/Drawer";
import { TextField } from "@/components/TextField/TextField";
import { rudderAnalytics, HotelFiltersTrackingEvents } from "@/lib/rudderstack";

type SearchByNameContentsProps = {
  value: string;
  onSubmit: (newQuery: string) => void;
  onClose: () => void;
};

export const SearchByNameContents = ({
  value,
  onSubmit,
  onClose,
}: SearchByNameContentsProps) => {
  const intl = useIntl();
  const [query, setQuery] = useState(value);

  return (
    <>
      <DrawerTopBar
        onCloseClick={onClose}
        title={intl.formatMessage({
          defaultMessage: "Search by name",
          description: "Label for the hotel name search input field in filters",
        })}
        actionElement={
          <DrawerTopBarActionButton
            onClick={() => {
              setQuery("");
            }}
          >
            {intl.formatMessage({
              defaultMessage: "Clear filter",
              description: "Button to remove a single active filter",
            })}
          </DrawerTopBarActionButton>
        }
      />

      <DrawerContent>
        <div className="mb-8">
          <TextField
            label={intl.formatMessage({
              defaultMessage: "Enter hotel name",
              description: "Placeholder text for the hotel name search input",
            })}
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </DrawerContent>

      <DrawerFooter>
        <Button
          fullWidth
          onClick={() => {
            const trimmedQuery = query.trim();
            rudderAnalytics.track(
              HotelFiltersTrackingEvents.USER_CLICKED_ON_SEARCHBYNAME_HOTEL,
              {
                hotel_name_entered: trimmedQuery,
                is_empty_search: trimmedQuery === "",
              },
            );
            onSubmit(trimmedQuery);
          }}
          variant="contained"
          size="large"
        >
          {intl.formatMessage({
            defaultMessage: "Apply",
            description: "Button to apply selected filters or changes",
          })}
        </Button>
      </DrawerFooter>
    </>
  );
};

type Props = SearchByNameContentsProps & {
  open: boolean;
};

export const SearchByNameDrawer = ({
  open,
  value,
  onSubmit,
  onClose,
}: Props) => {
  return (
    <SwipeableDrawer open={open} onClose={onClose}>
      <SearchByNameContents
        value={value}
        onSubmit={onSubmit}
        onClose={onClose}
      />
    </SwipeableDrawer>
  );
};
